import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, TrendingUp, Mail, Users, CheckCircle2, Calendar, ArrowRight, Loader2, Package } from 'lucide-react';
import { getAuthToken } from '../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIBriefing = ({ leads, isDark }) => {
  const [briefing, setBriefing] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [inventoryHint, setInventoryHint] = useState(null); // { leadName, item }
  const navigate = useNavigate();

  useEffect(() => {
    generateBriefing();
  }, [leads]);

  const generateBriefing = () => {
    setIsLoading(true);
    
    // Calculate briefing data from leads
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    const newLeadsToday = leads.filter(l => {
      const created = new Date(l.created_at);
      return created >= today;
    });

    const newLeadsThisWeek = leads.filter(l => {
      const created = new Date(l.created_at);
      return created >= weekAgo;
    });

    const hotLeads = leads.filter(l => l.intent_score >= 80 && l.status === 'new');
    const contactedLeads = leads.filter(l => l.status === 'contacted' || l.status === 'added_to_crm');
    const qualifiedLeads = leads.filter(l => l.status === 'qualified');

    // Determine priority action
    let priorityAction = null;
    if (hotLeads.length > 0) {
      priorityAction = {
        type: 'hot_leads',
        message: `You have ${hotLeads.length} hot lead${hotLeads.length > 1 ? 's' : ''} (80+ score) waiting to be contacted!`,
        action: 'Review Hot Leads',
        icon: TrendingUp,
        color: 'text-emerald-500',
        onClick: () => navigate('/dealer?tab=pipeline')
      };
    } else if (newLeadsToday.length > 0) {
      priorityAction = {
        type: 'new_leads',
        message: `${newLeadsToday.length} new lead${newLeadsToday.length > 1 ? 's' : ''} came in today. Ready to reach out?`,
        action: 'View New Leads',
        icon: Users,
        color: 'text-blue-500',
        onClick: () => navigate('/dealer?tab=pipeline')
      };
    } else if (contactedLeads.length > 0) {
      priorityAction = {
        type: 'follow_up',
        message: `${contactedLeads.length} lead${contactedLeads.length > 1 ? 's' : ''} waiting for follow-up.`,
        action: 'Follow Up',
        icon: Mail,
        color: 'text-amber-500',
        onClick: () => navigate('/dealer?tab=email')
      };
    }

    setBriefing({
      newLeadsToday: newLeadsToday.length,
      newLeadsThisWeek: newLeadsThisWeek.length,
      hotLeads: hotLeads.length,
      contactedPending: contactedLeads.length,
      qualifiedLeads: qualifiedLeads.length,
      priorityAction,
      greeting: getGreeting(),
    });
    
    setIsLoading(false);
  };

  // Inventory-aware talking point: when there IS a hot lead + the dealer
  // has uploaded inventory, fetch the top local match for the hottest
  // lead and surface a one-line "you have a matching unit" nudge so the
  // opening line writes itself.
  useEffect(() => {
    (async () => {
      const hotLeads = (leads || []).filter(l => l.intent_score >= 80 && l.status === 'new');
      if (hotLeads.length === 0) { setInventoryHint(null); return; }
      const topLead = hotLeads[0];
      if (!topLead?.id) return;
      try {
        const token = getAuthToken();
        const r = await axios.get(`${API}/dealer/lead/${topLead.id}/local-matches`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const match = r.data?.matches?.[0];
        if (match) {
          const parts = [match.year, match.make, match.model].filter(Boolean).join(' ');
          if (parts) setInventoryHint({ leadName: topLead.name || topLead.email, item: parts, stock: match.stock_number });
        } else {
          setInventoryHint(null);
        }
      } catch {
        setInventoryHint(null);
      }
    })();
  }, [leads]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (isLoading) {
    return (
      <div className="rounded-2xl p-6 bg-white/[0.03] border border-white/10 backdrop-blur-sm">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="animate-spin text-amber-400" size={24} />
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#0b1428] via-[#0b1020] to-[#05070d] backdrop-blur-sm shadow-[0_30px_80px_-30px_rgba(56,189,248,0.25)]"
      data-testid="ai-briefing"
    >
      {/* Ambient glow */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute -top-16 -left-12 w-64 h-64 rounded-full bg-cyan-500/15 blur-[80px]" />
        <div className="absolute -bottom-12 -right-12 w-64 h-64 rounded-full bg-amber-500/10 blur-[80px]" />
      </div>
      {/* Header */}
      <div className="relative p-5 pb-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-400/30 backdrop-blur flex items-center justify-center">
            <Sparkles size={20} className="text-amber-300" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">{briefing.greeting}!</h2>
            <p className="text-sm text-slate-400">Here's your daily briefing</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <StatCard 
            icon={Users} 
            label="New Today" 
            value={briefing.newLeadsToday} 
            highlight={briefing.newLeadsToday > 0}
          />
          <StatCard 
            icon={TrendingUp} 
            label="Hot Leads" 
            value={briefing.hotLeads} 
            highlight={briefing.hotLeads > 0}
            highlightColor="text-emerald-400"
          />
          <StatCard 
            icon={Mail} 
            label="Follow-ups" 
            value={briefing.contactedPending} 
          />
          <StatCard 
            icon={CheckCircle2} 
            label="Qualified Leads" 
            value={briefing.qualifiedLeads} 
            highlightColor="text-amber-400"
          />
        </div>
      </div>

      {/* Priority Action */}
      {briefing.priorityAction && (
        <div className="relative px-5 py-4 bg-white/5 backdrop-blur-sm border-t border-white/10">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center shrink-0">
                <briefing.priorityAction.icon size={20} className={briefing.priorityAction.color} />
              </div>
              <p className="text-sm font-medium text-white">
                {briefing.priorityAction.message}
              </p>
            </div>
            <button
              onClick={briefing.priorityAction.onClick}
              className="shrink-0 flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-[#F2A900] text-black text-sm font-bold hover:bg-amber-400 transition-colors cursor-pointer w-full sm:w-auto"
              data-testid="briefing-action-btn"
            >
              {briefing.priorityAction.action}
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Inventory-aware talking point — one-line nudge showing a real
          unit from the dealer's uploaded inventory that fits the hottest
          lead. Only renders when both a hot lead + local match exist. */}
      {inventoryHint && (
        <div
          className="relative px-5 py-3 bg-fuchsia-500/10 border-t border-fuchsia-500/20 flex items-center gap-3"
          data-testid="briefing-inventory-hint"
        >
          <div className="w-8 h-8 rounded-lg bg-fuchsia-500/20 border border-fuchsia-400/30 flex items-center justify-center shrink-0">
            <Package size={14} className="text-fuchsia-300" />
          </div>
          <p className="text-sm text-fuchsia-100 leading-tight">
            Opening line for <span className="font-bold text-white">{inventoryHint.leadName}</span>:{' '}
            <span className="italic">
              &ldquo;I have a <span className="font-bold text-fuchsia-200">{inventoryHint.item}</span>
              {inventoryHint.stock ? <> (stock #{inventoryHint.stock})</> : null} on the lot that lines up with what you told us.&rdquo;
            </span>
          </p>
        </div>
      )}

      {/* Weekly Summary */}
      <div className="relative px-5 py-3 flex items-center gap-2 text-xs bg-black/30 text-slate-400 border-t border-white/10">
        <Calendar size={12} />
        <span>{briefing.newLeadsThisWeek} new leads this week</span>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, highlight, highlightColor = 'text-cyan-300' }) => (
  <div className="bg-white/5 border border-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
    <Icon size={16} className={`mx-auto mb-1 ${highlight ? highlightColor : 'text-slate-400'}`} />
    <p className={`text-xl font-bold ${highlight ? 'text-white' : 'text-slate-200'}`}>{value}</p>
    <p className="text-xs text-slate-500">{label}</p>
  </div>
);

export default AIBriefing;
